'use client';

import { createTheme } from '@mui/material/styles';
import { borderRadius, color, fontSize, fontWeight, lineHeight, opacity, shadowSharp, spacing } from './tokens';
import { typography } from './typography';
import { ListItemButtonProps } from '@mui/material';

const DIALOG_TOP_AND_BOTTOM_MARGIN = 20;

const theme = createTheme({
    typography,
    breakpoints: {
        values: {
            xs: 0,
            sm: 768,
            md: 1200,
            lg: 1392,
            xl: 1536,
        },
    },
});

theme.components = {
    MuiContainer: {
        styleOverrides: {
            root: {
                paddingLeft: '24px',
                paddingRight: '24px',
                [theme.breakpoints.up('sm')]: {
                    paddingLeft: '32px',
                    paddingRight: '32px',
                },
                [theme.breakpoints.up('md')]: {
                    paddingLeft: '40px',
                    paddingRight: '40px',
                },
            },
        },
    },
    MuiTooltip: {
        styleOverrides: {
            tooltip: {
                background: color.gray[80],
                fontSize: fontSize[12],
                padding: `${spacing[4]} ${spacing[8]}`,
                borderRadius: borderRadius[4],
                boxSizing: 'border-box',
            },
            arrow: {
                color: color.gray[80],
            },
            popper: {
                '&[data-popper-placement*="bottom"] .MuiTooltip-tooltip': {
                    marginTop: '9px !important',
                },
                '&[data-popper-placement*="top"] .MuiTooltip-tooltip': {
                    marginBottom: '9px !important',
                },
                '&[data-popper-placement*="right"] .MuiTooltip-tooltip': {
                    marginLeft: '6px !important',
                },
                '&[data-popper-placement*="left"] .MuiTooltip-tooltip': {
                    marginRight: '6px !important',
                },
            },
        },
    },
    MuiPaper: {
        styleOverrides: {
            root: {
                borderRadius: borderRadius[8],
                boxShadow: shadowSharp[4],
                marginTop: '2px',

                '& .MuiMenuItem-root': {
                    paddingTop: '0 !important',
                },
            },
        },
    },
    MuiMenuItem: {
        styleOverrides: {
            root: {
                height: '36px',
                ...typography.label5regular,

                '&:hover': {
                    background: color.gray[5],
                },
                '&.Mui-selected': {
                    background: color.gray[10],
                    '&:hover': {
                        background: color.gray[10],
                    },
                    '&.Mui-focusVisible': {
                        background: color.gray[10],
                    },
                },
                '&.MuiAutocomplete-option[aria-selected="true"]': {
                    background: color.gray[10],
                    '&:hover': {
                        background: color.gray[10],
                    },
                    '&.Mui-focusVisible': {
                        background: color.gray[10],
                    },
                    '&.Mui-focused': {
                        background: color.gray[10],
                    },
                },
            },
        },
    },
    MuiAutocomplete: {
        styleOverrides: {
            option: {
                height: '36px',
                ...typography.label5regular,

                '&:hover': {
                    background: color.gray[5],
                },
                '&.Mui-selected': {
                    background: color.gray[10],
                    '&:hover': {
                        background: color.gray[10],
                    },
                    '&.Mui-focusVisible': {
                        background: color.gray[10],
                    },
                },
                '&.MuiAutocomplete-option[aria-selected="true"]': {
                    background: color.gray[10],
                    '&:hover': {
                        background: color.gray[10],
                    },
                    '&.Mui-focusVisible': {
                        background: color.gray[10],
                    },
                    '&.Mui-focused': {
                        background: color.gray[10],
                    },
                },
            },
            noOptions: {
                ...typography.label5regular,
                color: color.gray[70],
            },
            loading: {
                ...typography.label5regular,
                color: color.gray[70],
            },
        },
    },
    MuiSelect: {
        styleOverrides: {
            root: {
                color: color.gray[80],
            },
        },
    },
    MuiChip: {
        styleOverrides: {
            root: {
                background: color.gray[50],
                fontWeight: fontWeight.medium,
                fontSize: fontSize[13],
                lineHeight: lineHeight[130],
                letterSpacing: 0,
                color: color.white[0],
                height: '34px',
                borderRadius: borderRadius.round,

                '& .MuiChip-icon': {
                    color: color.white[0],
                    width: '18px',
                    height: '18px',
                    margin: 0,
                    paddingLeft: spacing[8],

                    '& + .MuiChip-label': {
                        paddingLeft: spacing[6],
                    },
                },
                '&.Mui-focusVisible': {
                    outline: `2px solid ${color.gray[80]}`,
                    outlineOffset: '2px',
                    backgroundColor: 'inherit',
                },
            },
        },
    },
    MuiDialog: {
        styleOverrides: {
            root: {
                '& .MuiPaper-root': {
                    borderRadius: borderRadius[8],
                    marginTop: `${DIALOG_TOP_AND_BOTTOM_MARGIN}px !important`,
                    marginBottom: `${DIALOG_TOP_AND_BOTTOM_MARGIN}px !important`,
                    maxHeight: `calc(100% - ${DIALOG_TOP_AND_BOTTOM_MARGIN * 2}px) !important`,
                },
            },
        },
    },
    MuiDialogContent: {
        styleOverrides: {
            root: {
                paddingRight: `${spacing[32]} !important`,
                paddingLeft: `${spacing[32]} !important`,
                paddingTop: `${spacing[32]} !important`,
                paddingBottom: `${spacing[32]} !important`,
            },
        },
    },
    MuiDialogTitle: {
        styleOverrides: {
            root: {
                height: 64,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                paddingRight: spacing[96],
                paddingLeft: spacing[32],
                paddingTop: 0,
                paddingBottom: 0,
                fontWeight: fontWeight.bold,
                fontSize: fontSize[20],
                lineHeight: lineHeight[125],
                letterSpacing: 0,
                color: color.gray[70],
            },
        },
    },
    MuiIconButton: {
        defaultProps: {
            disableFocusRipple: true,
        },
    },
    MuiSwitch: {
        defaultProps: {
            disableRipple: true,
        },
    },
    MuiTabs: {
        styleOverrides: {
            indicator: {
                backgroundColor: color.brand[50],
            },
            root: {
                height: 'auto',
                minHeight: 'auto',
                '& .MuiTabs-flexContainer': {
                    gap: spacing[16],
                },
                '&:focus-visible': {
                    outline: 'none',
                },
            },
        },
    },
    MuiTab: {
        defaultProps: {
            disableRipple: true,
        },
        styleOverrides: {
            root: {
                textTransform: 'none',
                ...typography.label5semibold,
                color: color.gray[70],
                padding: `0 0 ${spacing[6]} 0`,
                height: 'auto',
                minHeight: 'auto',
                minWidth: 'auto',
                '&:hover': {
                    color: color.gray[80],
                },
                '&.Mui-selected': {
                    color: color.brand[50],
                },
                '&.Mui-focusVisible': {
                    color: color.brand[50],
                    outline: 'none',
                },
            },
        },
    },
    MuiFormControlLabel: {
        styleOverrides: {
            root: {
                padding: '0 !important',
                margin: '0 !important',
                border: 'none !important',
            },
        },
    },
    MuiAccordionSummary: {
        styleOverrides: {
            root: {
                '&.Mui-focusVisible': {
                    backgroundColor: 'transparent',
                    outline: `2px solid ${color.gray[80]}`,
                    outlineOffset: '2px',
                    borderRadius: borderRadius[8],
                },
            },
        },
    },
};

export default theme;
